import { Injectable, signal } from '@angular/core';

/**
 * Reference-counted global HTTP loader. Multiple concurrent calls keep the
 * overlay visible until the last one completes (success, error, or cancel).
 *
 * A short minimum display time keeps the Lottie visible when responses are
 * instant (e.g. browser cache, cached NgRx) so it does not vanish before paint.
 * Each new load cycle (0 → 1) bumps {@link lottieMountId} so the animation
 * is recreated reliably when returning to a route.
 */
@Injectable({ providedIn: 'root' })
export class GlobalLoaderService {
  /** Ensures a visible frame even for sub-millisecond or cached "requests". */
  private static readonly MIN_VISIBLE_MS = 300;

  private activeRequests = 0;
  private readonly _visible = signal(false);
  private readonly _lottieMountId = signal(0);
  private shownAt: number | null = null;
  private hideWhenIdleHandle: ReturnType<typeof setTimeout> | null = null;

  /** When true, the root template shows the Lottie overlay. */
  readonly visible = this._visible.asReadonly();

  /** Bumped whenever loading starts from idle; use to recreate `ng-lottie` each cycle. */
  readonly lottieMountId = this._lottieMountId.asReadonly();

  begin(): void {
    this.clearPendingHide();
    const wasIdle = this.activeRequests === 0;
    this.activeRequests++;
    if (wasIdle) {
      this._visible.set(true);
      this.shownAt = Date.now();
      this._lottieMountId.update((n) => n + 1);
    }
  }

  end(): void {
    this.activeRequests = Math.max(0, this.activeRequests - 1);
    if (this.activeRequests === 0) {
      this.scheduleHideWhenIdle();
    }
  }

  private clearPendingHide(): void {
    if (this.hideWhenIdleHandle === null) {
      return;
    }
    clearTimeout(this.hideWhenIdleHandle);
    this.hideWhenIdleHandle = null;
  }

  private scheduleHideWhenIdle(): void {
    this.clearPendingHide();
    if (this.activeRequests !== 0) {
      return;
    }
    const since =
      this.shownAt != null
        ? Date.now() - this.shownAt
        : GlobalLoaderService.MIN_VISIBLE_MS;
    const remain = Math.max(0, GlobalLoaderService.MIN_VISIBLE_MS - since);

    const runHide = (): void => {
      this.hideWhenIdleHandle = null;
      if (this.activeRequests === 0) {
        this._visible.set(false);
        this.shownAt = null;
      }
    };

    this.hideWhenIdleHandle = setTimeout(runHide, remain);
  }
}
